import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext.js';
import { useAuth } from './contexts/AuthContext.js';
import Billing from './pages/Billing.js';
import Dashboard from './pages/Dashboard.js';
import Users from './pages/Users.js';
import Cards from './pages/Cards.js';
import Departments from './pages/Departments.js';
import Settings from './pages/Settings.js';
import Security from './pages/Security.js';
import OrganisationsPage from './pages/OrganisationsPage.js';
import LoginPage from './pages/LoginPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage.js';
import InvitationAcceptancePage from './pages/InvitationAcceptancePage.js';
import PublicCardPage from './pages/PublicCardPage.js';
import CardEditor from './pages/CardEditor.js';
import SuperAdminUsersPage from './pages/SuperAdminUsersPage.js';
import Help from './pages/Help.js';
import Analytics from './pages/Analytics.js';
import LinkPages from './pages/LinkPages.js';
import LinkPageEditor from './pages/LinkPageEditor.js';
import LinkPagePublic from './pages/LinkPagePublic.js';
import LinkPageWizard from './pages/LinkPageWizard.js';
import Leads from './pages/Leads.js';
import OcrLearning from './pages/OcrLearning.js';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="h-6 w-6 rounded-full border-2 border-accent-500 border-t-transparent animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function UsersRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === 'superadmin') return <SuperAdminUsersPage />;
  return <Users />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Protected admin / dashboard routes */}
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UsersRouter /></ProtectedRoute>} />
        <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
        <Route path="/cards/new" element={<ProtectedRoute><CardEditor /></ProtectedRoute>} />
        <Route path="/cards/:slug" element={<ProtectedRoute><CardEditor /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
        <Route path="/ocr-learning" element={<ProtectedRoute><OcrLearning /></ProtectedRoute>} />
        <Route path="/links" element={<ProtectedRoute><LinkPages /></ProtectedRoute>} />
        <Route path="/links/new" element={<ProtectedRoute><LinkPageWizard /></ProtectedRoute>} />
        <Route path="/links/:id" element={<ProtectedRoute><LinkPageEditor /></ProtectedRoute>} />
        <Route path="/departments" element={<ProtectedRoute><Departments /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        <Route path="/organisations" element={<ProtectedRoute><OrganisationsPage /></ProtectedRoute>} />

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<InvitationAcceptancePage />} />

        {/* Public link page (must come before the org-scoped card route) */}
        <Route path="/l/:slug" element={<LinkPagePublic />} />

        {/* Public card routes */}
        <Route path="/:shortCode" element={<PublicCardPage />} />
        <Route path="/t/:trackSlug" element={<PublicCardPage />} />
        <Route path="/:orgSlug/:cardSlug" element={<PublicCardPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
